export function buildExecutiveReasoning(item = {}) {

  // Usar estado ya calculado por el engine si existe
  const estadoTerritorial =
    item._calculatedState ||
    item.estadoTerritorial ||
    inferEstadoTerritorial(item);

  const narrativaDominante =
    item.narrativaDominante ||
    inferNarrativaDominante(item);

  const interpretacion =
    item.interpretacionEjecutiva ||
    buildInterpretacion(item, estadoTerritorial, narrativaDominante);

  const justificacion =
    toList(item.justificacionTerritorial) ||
    buildJustificacion(item);

  const evidencias =
    toList(item.evidenciasDetonantes) ||
    buildEvidencias(item);

  const riesgoPotencial =
    item.riesgoPotencial ||
    buildRiesgoPotencial(item, estadoTerritorial);

  const accionSugerida =
    item.accionSugerida ||
    buildAccionSugerida(item, estadoTerritorial);

  return {
    estadoTerritorial,
    narrativaDominante,
    interpretacion,
    justificacion,
    evidencias,
    riesgoPotencial,
    accionSugerida
  };
}

// ─────────────────────────────────────────────
// INFERENCIA DE ESTADO
// Solo se usa si el engine no calculó uno
// ─────────────────────────────────────────────

function inferEstadoTerritorial(item) {
  if (item.riesgo === 'Crítico') return 'Escalamiento Territorial';
  if (
    item.convergenciaNarrativa === 'Alta' ||
    item.convergenciaNarrativa === 'Crítica'
  ) return 'Convergencia Narrativa';
  if (
    item.activacionTerritorial === 'Alta' ||
    item.activacionTerritorial === 'Crítica'
  ) return 'Activación Territorial';
  if (
    item.sensibilidadTerritorial === 'Alta' ||
    item.sensibilidadTerritorial === 'Crítica'
  ) return 'Sensibilidad Creciente';
  if (item.riesgo === 'Alto') return 'Observación';
  return 'Estable';
}

// ─────────────────────────────────────────────
// NARRATIVA DOMINANTE
// ─────────────────────────────────────────────

function inferNarrativaDominante(item) {
  // Prioridad 1: narrativa dominante declarada explícitamente
  if (item.narrativaDominante?.trim()) {
    return item.narrativaDominante.trim();
  }

  // Prioridad 2: primera narrativa del array si existe
  if (Array.isArray(item.narrativas) && item.narrativas.length > 0) {
    const primera = item.narrativas[0];
    return typeof primera === 'string' ? primera.trim() : (primera?.texto?.trim() || 'Sin narrativa declarada');
  }

  // Sin narrativa declarada — no inventar
  return 'Sin narrativa declarada';
}

// ─────────────────────────────────────────────
// INTERPRETACIÓN EJECUTIVA
// Contextual por actor — no genérica
// ─────────────────────────────────────────────

function buildInterpretacion(item, estado, narrativa) {
  const nombre = item.nombre || 'El actor';
  const influencia = item.influencia || item.influenciaTerritorial || '';
  const sector = item.sector || item.comuna || '';
  const alertas = item._alertasVinculadas || 0;
  const señales = item._señalesVinculadas || 0;
  const scoring = item._scoring || {};

  const partes = [];

  // Contexto base
  const contexto = sector
    ? `${nombre} opera en ${sector}`
    : `${nombre}`;

  partes.push(`${contexto} y se encuentra en estado de ${estado}.`);

  // Narrativa
  partes.push(`La narrativa dominante asociada es "${narrativa}".`);

  // Influencia y propagación
  if (influencia) {
    const prop = item.capacidadPropagacion
      ? ` con capacidad de propagación ${item.capacidadPropagacion}`
      : '';
    partes.push(`Presenta influencia territorial ${influencia}${prop}.`);
  }

  // Señales externas detectadas
  if (alertas > 0 || señales > 0) {
    const partsSignal = [];
    if (alertas > 0) partsSignal.push(`${alertas} alerta${alertas > 1 ? 's' : ''} activa${alertas > 1 ? 's' : ''}`);
    if (señales > 0) partsSignal.push(`${señales} señal${señales > 1 ? 'es' : ''} RRSS vinculada${señales > 1 ? 's' : ''}`);
    partes.push(`Se detectaron ${partsSignal.join(' y ')} asociadas a este actor.`);
  }

  // IVT si disponible
  if (scoring.IVT !== undefined) {
    partes.push(`Índice de Viabilidad Territorial (IVT): ${scoring.IVT}%.`);
  }

  return partes.join(' ');
}

// ─────────────────────────────────────────────
// JUSTIFICACIÓN TERRITORIAL
// ─────────────────────────────────────────────

function buildJustificacion(item) {
  const result = [];

  if (item.influencia || item.influenciaTerritorial) {
    result.push(
      `Presenta influencia territorial ${item.influenciaTerritorial || item.influencia}.`
    );
  }
  if (item.sensibilidadTerritorial) {
    result.push(`Registra sensibilidad territorial ${item.sensibilidadTerritorial}.`);
  }
  if (item.convergenciaNarrativa) {
    result.push(`Tiene convergencia narrativa ${item.convergenciaNarrativa}.`);
  }
  if (item.capacidadPropagacion) {
    result.push(`Posee capacidad de propagación ${item.capacidadPropagacion}.`);
  }
  if (item.legitimidadTerritorial) {
    result.push(`Legitimidad territorial ${item.legitimidadTerritorial}.`);
  }
  if (item.tags) {
    result.push(`Etiquetas territoriales: ${item.tags}.`);
  }
  if (item._tieneAlertaCritica) {
    result.push('Tiene alerta crítica activa vinculada.');
  }

  return result.length > 0
    ? result
    : ['Información insuficiente para justificación robusta. Se recomienda enriquecer el perfil del actor.'];
}

// ─────────────────────────────────────────────
// EVIDENCIAS DETONANTES
// ─────────────────────────────────────────────

function buildEvidencias(item) {
  const result = [];

  if (item.narrativasAsociadas) {
    result.push(`Narrativas asociadas: ${item.narrativasAsociadas}.`);
  }
  if (item.canalesPropagacion) {
    result.push(`Canales de propagación: ${item.canalesPropagacion}.`);
  }
  if (item.observaciones) {
    result.push(`Observación registrada: ${item.observaciones}.`);
  }
  if (item._alertasVinculadas > 0) {
    result.push(`${item._alertasVinculadas} alerta(s) territorial(es) vinculada(s) al actor.`);
  }
  if (item._señalesVinculadas > 0) {
    result.push(`${item._señalesVinculadas} señal(es) RRSS vinculada(s) al actor.`);
  }

  return result.length > 0
    ? result
    : ['Sin evidencias detonantes registradas. Incorporar señales territoriales para enriquecer el análisis.'];
}

// ─────────────────────────────────────────────
// RIESGO POTENCIAL
// ─────────────────────────────────────────────

function buildRiesgoPotencial(item, estado) {
  const influencia = String(item.influencia || '').toLowerCase();
  const propagacion = String(item.capacidadPropagacion || '').toLowerCase();
  const esInfluyente = influencia === 'alta' || propagacion === 'alta' || propagacion === 'crítica';

  if (estado === 'Escalamiento Territorial') {
    return esInfluyente
      ? 'Riesgo crítico. Actor con alta influencia en escalamiento activo — afectación directa a viabilidad territorial.'
      : 'Riesgo alto de afectación directa a la viabilidad territorial del proyecto.';
  }
  if (estado === 'Convergencia Narrativa') {
    return esInfluyente
      ? 'Riesgo alto. Actor influyente en convergencia narrativa — puede acelerar movilización territorial.'
      : 'Riesgo de expansión narrativa hacia actores o territorios relacionados.';
  }
  if (estado === 'Activación Territorial') {
    return 'Riesgo de coordinación territorial si no existe gestión preventiva inmediata.';
  }
  if (estado === 'Sensibilidad Creciente') {
    return 'Riesgo de consolidación narrativa si aumenta la exposición territorial.';
  }
  if (estado === 'Observación') {
    return 'Riesgo bajo. Requiere seguimiento para detectar evolución territorial.';
  }

  return 'Riesgo bajo. Sin señales territoriales relevantes actualmente.';
}

// ─────────────────────────────────────────────
// ACCIÓN SUGERIDA
// ─────────────────────────────────────────────

function buildAccionSugerida(item, estado) {
  const influencia = String(item.influencia || '').toLowerCase();
  const esInfluyente = influencia === 'alta';

  if (estado === 'Escalamiento Territorial') {
    return esInfluyente
      ? 'Activar protocolo de gestión territorial prioritaria. Reunión directa urgente con actor clave y estrategia de contención.'
      : 'Activar gestión territorial prioritaria, reunión directa y estrategia de contención.';
  }
  if (estado === 'Convergencia Narrativa') {
    return 'Realizar acercamiento preventivo, mapear actores relacionados y preparar comunicación territorial.';
  }
  if (estado === 'Activación Territorial') {
    return esInfluyente
      ? 'Contacto directo prioritario. Levantar información territorial y activar monitoreo reforzado.'
      : 'Contactar al stakeholder y levantar información territorial directa.';
  }
  if (estado === 'Sensibilidad Creciente') {
    return 'Monitorear evolución narrativa y preparar mensaje preventivo territorial.';
  }
  if (estado === 'Observación') {
    return 'Mantener monitoreo regular y registrar nuevas señales territoriales.';
  }

  return 'Mantener monitoreo territorial periódico.';
}

// ─────────────────────────────────────────────
// UTILIDADES
// ─────────────────────────────────────────────

function toList(value) {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  return String(value)
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
}
